/**
 * Blocks Skylite until user redeems a role license (InstitutionRoleLicenseCodes).
 * Owner/tester accounts skip via API personalLicense.status === 'active'.
 */
import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import {
  normalizeAndValidateLicenseCode,
  sanitizeLicenseCodeInput
} from '../lib/licenseCodeFormat.js';

export default function LicenseGateOverlay({
  personalLicense,
  onRedeemed,
  onClosePreview,
  onRequestLocalDevLicenseSkip
}) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');

  if (!personalLicense) return null;
  const { status, expiresAt, isTrial, trialTermDays } = personalLicense;
  if (status === 'active') return null;

  const isRenewal = status === 'expired';
  const trialDaysLabel = Number(trialTermDays) > 0 ? Number(trialTermDays) : 7;
  const title = isRenewal
    ? isTrial
      ? 'Your trial has ended'
      : 'Renew your access'
    : 'Activate your license';
  const body = isRenewal
    ? isTrial
      ? `Your ${trialDaysLabel}-day trial has ended. Enter a new license code below, or contact us to continue.`
      : 'Your previous license term has ended. To continue using Skylite and training, renew with your organization or enter a new license code below.'
    : 'Enter the license code you received. Your role (student, teacher, or admin) is set by that code.';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    // Lock the field immediately on click — no double-submit / race window.
    setSubmitting(true);
    setLocalError('');

    const normalized = normalizeAndValidateLicenseCode(code);
    if (!normalized) {
      setLocalError('Enter a valid license code (example: SPX-STD-XXXX-XXXX-XXXX-XXXX).');
      setSubmitting(false);
      return;
    }

    try {
      const r = await fetch('/api/license/redeem', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ licenseCode: normalized })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const errKey = data.error ? String(data.error) : '';
        if (errKey === 'license_redeem_timeout' || data.message) {
          setLocalError(String(data.message || 'Database timed out. Close any open SSMS transactions on this database, then try again.'));
        } else if (errKey === 'institution_license_inactive') {
          setLocalError('This organization license is not active yet. Ask your admin, or try again shortly.');
        } else if (errKey === 'invalid_license_code') {
          setLocalError('That license code is not valid.');
        } else if (errKey === 'license_email_mismatch') {
          setLocalError(
            data.message ||
              'This code is locked to a different email. Sign in with the Google account that received the invite.'
          );
        } else if (errKey === 'license_email_required') {
          setLocalError('Sign in with Google first, then enter your trial code.');
        } else {
          setLocalError(errKey ? errKey.replace(/_/g, ' ') : 'Redeem failed.');
        }
        setSubmitting(false);
        return;
      }
      // Stay locked on success — overlay is replaced by welcome / redirect.
      setCode('');
      onRedeemed?.(data);
    } catch {
      setLocalError('Network error.');
      setSubmitting(false);
    }
  };

  return (
    <Box
      className="notranslate"
      translate="no"
      dir="ltr"
      lang="en"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 50000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        background: 'linear-gradient(145deg, rgba(6, 12, 28, 0.97) 0%, rgba(12, 20, 40, 0.98) 100%)',
        backdropFilter: 'blur(6px)'
      }}
    >
      <Paper
        component="form"
        className="notranslate"
        translate="no"
        dir="ltr"
        lang="en"
        onSubmit={handleSubmit}
        elevation={8}
        sx={{
          maxWidth: 440,
          width: '100%',
          p: 3,
          borderRadius: 2,
          border: '1px solid rgba(130, 210, 255, 0.28)',
          background: 'linear-gradient(160deg, rgba(18, 28, 52, 0.98), rgba(10, 16, 32, 0.99))'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#e8f6ff', mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(220, 240, 255, 0.88)', mb: 2, lineHeight: 1.65 }}>
          {body}
        </Typography>
        {expiresAt ? (
          <Typography variant="caption" sx={{ display: 'block', color: 'rgba(180, 210, 230, 0.75)', mb: 2 }}>
            Previous term ended: {new Date(expiresAt).toLocaleString()}
          </Typography>
        ) : null}
        <TextField
          fullWidth
          autoComplete="off"
          label="License code"
          value={code}
          onChange={(ev) => {
            if (submitting) return;
            setLocalError('');
            setCode(sanitizeLicenseCodeInput(ev.target.value));
          }}
          disabled={submitting}
          placeholder="SPX-STD-XXXX-XXXX-XXXX-XXXX"
          helperText="Including hyphens when entering in your license."
          inputProps={{
            spellCheck: false,
            autoCorrect: 'off',
            autoCapitalize: 'characters',
            maxLength: 27,
            inputMode: 'text',
            pattern: 'SPX-(ADM|TCH|STD)-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{4}',
            'aria-label': 'License code'
          }}
          FormHelperTextProps={{ id: 'license-gate-hint', sx: { color: 'rgba(180, 210, 230, 0.78)' } }}
          error={Boolean(localError)}
          aria-invalid={Boolean(localError)}
          aria-describedby={localError ? 'license-gate-error' : 'license-gate-hint'}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(0,0,0,0.25)' },
            '& .MuiInputLabel-root': { color: 'rgba(180, 220, 255, 0.85)' },
            '& .MuiOutlinedInput-input': { color: '#f0f8ff' }
          }}
        />
        {localError ? (
          <Typography id="license-gate-error" variant="body2" role="alert" sx={{ color: '#ffb4a8', mb: 2 }}>
            {localError}
          </Typography>
        ) : null}
        <Button type="submit" variant="contained" disabled={submitting} fullWidth sx={{ py: 1.2, fontWeight: 700 }}>
          {submitting ? 'Checking…' : 'Continue'}
        </Button>
        {onClosePreview ? (
          <Button
            type="button"
            variant="text"
            fullWidth
            onClick={() => onClosePreview()}
            sx={{ mt: 1.5, color: 'rgba(180, 210, 230, 0.85)', textTransform: 'none', fontSize: '0.82rem' }}
          >
            Close preview (owner)
          </Button>
        ) : null}
        <Button
          type="button"
          variant="text"
          fullWidth
          disabled={submitting}
          onClick={() => {
            window.location.href = '/logout';
          }}
          sx={{ mt: 1.25, color: 'rgba(160, 195, 230, 0.88)', textTransform: 'none', fontSize: '0.8rem' }}
        >
          Log out — use another account
        </Button>
        {typeof onRequestLocalDevLicenseSkip === 'function' && !onClosePreview ? (
          <Button
            type="button"
            variant="outlined"
            fullWidth
            disabled={submitting}
            onClick={() => onRequestLocalDevLicenseSkip()}
            sx={{
              mt: 1.5,
              textTransform: 'none',
              fontSize: '0.78rem',
              fontWeight: 700,
              borderColor: 'rgba(180, 220, 255, 0.45)',
              color: 'rgba(200, 235, 255, 0.95)',
              '&:hover': { borderColor: 'rgba(220, 240, 255, 0.65)' }
            }}
          >
            Skip license gate (local dev only)
          </Button>
        ) : null}
      </Paper>
    </Box>
  );
}
